import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from '../config/axios';

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const tableStyles = {
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  background: '#fff',
};
const thStyles = {
  background: '#f7fafc',
  color: '#333',
  fontWeight: 600,
  padding: '12px 10px',
  borderBottom: '1px solid #e2e8f0',
  textAlign: 'left',
};
const tdStyles = {
  padding: '10px',
  borderBottom: '1px solid #f1f1f1',
  verticalAlign: 'middle',
};
const actionBtn = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  margin: '0 4px',
  fontSize: '1.1rem',
};

const AdsPage = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    client: '',
    title: '',
    subtitle: '',
    buttonLabel: '',
    url: '',
    adType: 'Monthly',
    adStartDate: '',
    adEndDate: '',
    priority: 1,
    impressionTrackUrl: '',
    clickTrackUrl: '',
    isSponsored: false,
    isPremiumOnly: false,
    tag: '',
    backgroundColor: '',
    textColor: '',
    ctaStyle: 'filled',
    trackingCode: '',
    visibilityRule: 'all',
    showDismiss: false,
    placement: '',
    bannerImage: null,
    // NEW: Location targeting fields
    locationTargeting: {
      type: 'none', // 'none' for global, 'radius' for location-based
      coordinates: null,
      radiusKm: null
    }
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef();
  const [editAd, setEditAd] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteAdId, setDeleteAdId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [locationSearchResults, setLocationSearchResults] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationSearchValue, setLocationSearchValue] = useState('');
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(-1);
  const [locationSearchLoading, setLocationSearchLoading] = useState(false);
  const [locationSearchError, setLocationSearchError] = useState('');
  const API_BASE_URL = 'https://api.aarogai.com/api';
  
  // Configuration: Set to false to disable API search while backend is being developed
  // Set to true when the backend API endpoint /admin/ads/location-search is ready
  const ENABLE_API_SEARCH = false;

  // Debounce the search value to prevent excessive API calls
  // This ensures we only make API calls after the user stops typing for 500ms
  // This improves performance and reduces unnecessary network requests
  const debouncedSearchValue = useDebounce(locationSearchValue, 500); // 500ms delay

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    console.log('getImageUrl called with:', imagePath);
    if (!imagePath) return null;
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      console.log('Returning full URL:', imagePath);
      return imagePath;
    }
    // If it's just a filename, construct the full URL
    const fullUrl = `${API_BASE_URL}/assets/ads/${imagePath}`;
    console.log('Constructed URL:', fullUrl);
    return fullUrl;
  };

  // NEW: Handle location targeting changes
  const handleLocationTargetingChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      locationTargeting: {
        ...prev.locationTargeting,
        [field]: value
      }
    }));
    
    // Clear location search when switching to global targeting
    if (field === 'type' && value === 'none') {
      setLocationSearchValue('');
      setLocationSearchResults([]);
      setShowLocationDropdown(false);
    }
  };

  // NEW: Handle coordinate changes
  const handleCoordinateChange = (coord, value) => {
    setForm(prev => ({
      ...prev,
      locationTargeting: {
        ...prev.locationTargeting,
        coordinates: {
          ...prev.locationTargeting.coordinates,
          [coord]: parseFloat(value) || null
        }
      }
    }));
  };

  // NEW: Handle edit location targeting changes
  const handleEditLocationTargetingChange = (field, value) => {
    setEditAd(prev => ({
      ...prev,
      locationTargeting: {
        ...prev.locationTargeting,
        [field]: value
      }
    }));
    
    // Clear location data when switching to global targeting
    if (field === 'type' && value === 'none') {
      setEditAd(prev => ({
        ...prev,
        locationTargeting: {
          ...prev.locationTargeting,
          coordinates: null,
          radiusKm: null
        }
      }));
    }
  };

  // NEW: Handle edit coordinate changes
  const handleEditCoordinateChange = (coord, value) => {
    setEditAd(prev => ({
      ...prev,
      locationTargeting: {
        ...prev.locationTargeting,
        coordinates: {
          ...prev.locationTargeting.coordinates,
          [coord]: parseFloat(value) || null
        }
      }
    }));
  };

  // NEW: Auto-search location functions
  const cityCoordinates = {
    'mumbai': { lat: 19.0760, lng: 72.8777, display: 'Mumbai, Maharashtra' },
    'delhi': { lat: 28.7041, lng: 77.1025, display: 'Delhi, NCR' },
    'bangalore': { lat: 12.9716, lng: 77.5946, display: 'Bangalore, Karnataka' },
    'chennai': { lat: 13.0827, lng: 80.2707, display: 'Chennai, Tamil Nadu' },
    'kolkata': { lat: 22.5726, lng: 88.3639, display: 'Kolkata, West Bengal' },
    'hyderabad': { lat: 17.3850, lng: 78.4867, display: 'Hyderabad, Telangana' },
    'pune': { lat: 18.5204, lng: 73.8567, display: 'Pune, Maharashtra' },
    'ahmedabad': { lat: 23.0225, lng: 72.5714, display: 'Ahmedabad, Gujarat' },
    'jaipur': { lat: 26.9124, lng: 75.7873, display: 'Jaipur, Rajasthan' },
    'lucknow': { lat: 26.8467, lng: 80.9462, display: 'Lucknow, Uttar Pradesh' },
    'nagpur': { lat: 21.1458, lng: 79.0882, display: 'Nagpur, Maharashtra' },
    'indore': { lat: 22.7196, lng: 75.8577, display: 'Indore, Madhya Pradesh' },
    'thane': { lat: 19.2183, lng: 72.9781, display: 'Thane, Maharashtra' },
    'bhopal': { lat: 23.2599, lng: 77.4126, display: 'Bhopal, Madhya Pradesh' },
    'visakhapatnam': { lat: 17.6868, lng: 83.2185, display: 'Visakhapatnam, Andhra Pradesh' },
    'patna': { lat: 25.5941, lng: 85.1376, display: 'Patna, Bihar' },
    'vadodara': { lat: 22.3072, lng: 73.1812, display: 'Vadodara, Gujarat' },
    'ghaziabad': { lat: 28.6692, lng: 77.4538, display: 'Ghaziabad, Uttar Pradesh' },
    'ludhiana': { lat: 30.9010, lng: 75.8573, display: 'Ludhiana, Punjab' },
    'agra': { lat: 27.1767, lng: 78.0081, display: 'Agra, Uttar Pradesh' }
  };



  // Debounced search effect - triggers search when debounced value changes
  useEffect(() => {
    if (debouncedSearchValue && debouncedSearchValue.length >= 2) {
      const performSearch = async () => {
        setLocationSearchLoading(true);
        setLocationSearchError('');
        
        try {
          // Try API search first, fallback to static
          const results = await searchLocationAPI(debouncedSearchValue);
          
          setLocationSearchResults(results);
          setShowLocationDropdown(results.length > 0);
          setSelectedLocationIndex(-1);
        } catch (error) {
          console.error('Location search error:', error);
          setLocationSearchError('Search failed, please try again');
        } finally {
          setLocationSearchLoading(false);
        }
      };

      performSearch();
    } else if (debouncedSearchValue.length < 2) {
      // Clear results if search term is too short
      setLocationSearchResults([]);
      setShowLocationDropdown(false);
      setLocationSearchError('');
    }
  }, [debouncedSearchValue]);

  // Simplified search handler - just updates the input value
  const handleLocationSearch = (searchTerm) => {
    setLocationSearchValue(searchTerm);
    
    // Clear results immediately if search term is too short
    if (searchTerm.length < 2) {
      setLocationSearchResults([]);
      setShowLocationDropdown(false);
      setLocationSearchError('');
    }
  };

  // Enhanced location selection
  const selectLocation = (location) => {
    setLocationSearchValue(location.display);
    handleCoordinateChange('lat', location.lat);
    handleCoordinateChange('lng', location.lng);
    setShowLocationDropdown(false);
    setLocationSearchResults([]);
    setSelectedLocationIndex(-1);
    setLocationSearchError('');
  };





  // Debounced edit search effect
  useEffect(() => {
    if (debouncedSearchValue && debouncedSearchValue.length >= 2) {
      const performEditSearch = () => {
        const results = searchStaticDatabase(debouncedSearchValue);
        
        // For edit form, we'll use a simpler approach
        if (results.length > 0) {
          const firstResult = results[0];
          handleEditCoordinateChange('lat', firstResult.lat);
          handleEditCoordinateChange('lng', firstResult.lng);
        }
      };

      performEditSearch();
    }
  }, [debouncedSearchValue]);

  // Simplified edit search handler
  const handleEditLocationSearch = (searchTerm) => {
    // Just update the search value - the effect will handle the actual search
    setLocationSearchValue(searchTerm);
  };

  // NEW: Keyboard navigation for location search
  const handleLocationKeyDown = (e) => {
    if (!showLocationDropdown || locationSearchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedLocationIndex(prev => 
          prev < locationSearchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedLocationIndex(prev => 
          prev > 0 ? prev - 1 : locationSearchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedLocationIndex >= 0 && selectedLocationIndex < locationSearchResults.length) {
          selectLocation(locationSearchResults[selectedLocationIndex]);
        }
        break;
      case 'Escape':
        setShowLocationDropdown(false);
        setSelectedLocationIndex(-1);
        break;
      default:
        break;
    }
  };

  const resetLocationSearch = () => {
    setLocationSearchValue('');
    setLocationSearchResults([]);
    setShowLocationDropdown(false);
    setSelectedLocationIndex(-1);
  };

  // NEW: API-based location search functions
  const searchLocationAPI = async (query) => {
    if (!query || query.length < 2) return [];
    
    // Skip API search if disabled
    if (!ENABLE_API_SEARCH) {
      console.log('API search disabled, using static search');
      setLocationSearchError('API search disabled, using local search');
      return searchStaticDatabase(query);
    }
    
    setLocationSearchLoading(true);
    setLocationSearchError('');
    
    try {
      // Try the new location search API first
      const response = await axios.get('/admin/ads/location-search', {
        params: { 
          location: query, 
          radius: 10, 
          limit: 8 
        }
      });
      
      if (response.data.success && response.data.data) {
        // Convert API response to our format
        const apiResults = response.data.data.map(ad => ({
          id: ad._id,
          display: `${ad.title} - ${ad.client}`,
          lat: ad.locationInfo?.userLocation?.lat || ad.locationTargeting?.coordinates?.lat,
          lng: ad.locationInfo?.userLocation?.lng || ad.locationTargeting?.coordinates?.lng,
          distance: ad.distanceKm,
          type: 'api_result'
        }));
        
        return apiResults;
      }
      
      return [];
    } catch (error) {
      console.log('API search failed, falling back to static search:', error.message);
      
      // Set appropriate error message based on error type
      if (error.response?.status === 500) {
        setLocationSearchError('Backend API temporarily unavailable, using local search');
      } else if (error.response?.status === 404) {
        setLocationSearchError('Location search API not found, using local search');
      } else if (error.code === 'NETWORK_ERROR') {
        setLocationSearchError('Network error, using local search');
      } else {
        setLocationSearchError('API error, using local search');
      }
      
      // Fallback to static search
      return searchStaticDatabase(query);
    } finally {
      setLocationSearchLoading(false);
    }
  };

  const searchStaticDatabase = (query) => {
    return Object.entries(cityCoordinates)
      .filter(([city, data]) => 
        city.toLowerCase().includes(query.toLowerCase()) ||
        data.display.toLowerCase().includes(query.toLowerCase())
      )
      .map(([city, data]) => ({
        id: city,
        display: data.display,
        lat: data.lat,
        lng: data.lng,
        distance: null,
        type: 'static_result'
      }))
      .slice(0, 8);
  };



  // File validation before upload
  const validateImageFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload JPG, PNG, GIF, or WebP.');
    }
    
    if (file.size > maxSize) {
      throw new Error('File too large. Maximum size is 5MB.');
    }
    
    return true;
  };

  // Add delay to prevent rate limiting
  const addDelay = (ms = 1000) => new Promise(resolve => setTimeout(resolve, ms));

  // Fetch all active ads
  const fetchAds = async () => {
    setLoading(true);
    setError('');
    try {
      // Add delay to prevent rate limiting
      await addDelay(500);
      const res = await axios.get('/admin/ads');
      // Handle both old and new response formats
      const adsData = res.data.ads || res.data.data || res.data || [];
      console.log('Fetched ads data:', adsData);
      // Log image paths for debugging
      adsData.forEach((ad, index) => {
        console.log(`Ad ${index + 1} image paths:`, {
          bannerImageUrl: ad.bannerImageUrl,
          bannerImage: ad.bannerImage,
          title: ad.title
        });
      });
      setAds(adsData);
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait 15 minutes and try again.');
      } else {
        setError('Failed to fetch ads.');
      }
      console.error('Error fetching ads:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAds();
  }, []); // Only fetch once when component mounts

  // NEW: Handle clicking outside location dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationDropdown && !event.target.closest('.location-search-container')) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationDropdown]);

  // Add a manual refresh function
  const handleRefresh = () => {
    fetchAds();
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      setForm((prev) => ({ ...prev, bannerImage: files[0] }));
      setFormError('');
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Validate image dimensions
  const validateImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = function () {
        if (img.width === 800 && img.height === 400) {
          resolve(true);
        } else {
          reject('Banner image must be exactly 800x400 pixels.');
        }
      };
      img.onerror = function () {
        reject('Invalid image file.');
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle form submit with improved error handling
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccess('');
    setSubmitting(true);

    // Validate required fields
    const requiredFields = [
      'client', 'title', 'buttonLabel', 'url', 'adType', 'adStartDate', 'adEndDate',
      'priority', 'ctaStyle', 'visibilityRule', 'placement'
    ];
    for (let field of requiredFields) {
      if (!form[field] || (typeof form[field] === 'string' && form[field].trim() === '')) {
        setFormError(`Field "${field}" is required.`);
        setSubmitting(false);
        return;
      }
    }
    
    // NEW: Validate location targeting fields
    if (form.locationTargeting.type === 'radius') {
      if (!form.locationTargeting.coordinates?.lat || !form.locationTargeting.coordinates?.lng) {
        setFormError('Latitude and longitude are required for location-based ads.');
        setSubmitting(false);
        return;
      }
      if (!form.locationTargeting.radiusKm || form.locationTargeting.radiusKm <= 0) {
        setFormError('Radius is required and must be greater than 0 for location-based ads.');
        setSubmitting(false);
        return;
      }
      // Validate coordinate ranges
      if (form.locationTargeting.coordinates.lat < -90 || form.locationTargeting.coordinates.lat > 90) {
        setFormError('Latitude must be between -90 and 90 degrees.');
        setSubmitting(false);
        return;
      }
      if (form.locationTargeting.coordinates.lng < -180 || form.locationTargeting.coordinates.lng > 180) {
        setFormError('Longitude must be between -180 and 180 degrees.');
        setSubmitting(false);
        return;
      }
      if (form.locationTargeting.radiusKm > 1000) {
        setFormError('Radius cannot exceed 1000 kilometers.');
        setSubmitting(false);
        return;
      }
    }
    // Validate boolean required fields
    if (typeof form.isSponsored !== 'boolean') {
      setFormError('Field "isSponsored" is required.');
      setSubmitting(false);
      return;
    }
    if (typeof form.isPremiumOnly !== 'boolean') {
      setFormError('Field "isPremiumOnly" is required.');
      setSubmitting(false);
      return;
    }
    if (typeof form.showDismiss !== 'boolean') {
      setFormError('Field "showDismiss" is required.');
      setSubmitting(false);
      return;
    }
    // Validate image is present
    if (!form.bannerImage) {
      setFormError('Banner image is required.');
      setSubmitting(false);
      return;
    }
    // Validate image file
    try {
      validateImageFile(form.bannerImage);
    } catch (err) {
      setFormError(err.message);
      setSubmitting(false);
      return;
    }
    // Validate image dimensions
    try {
      await validateImageDimensions(form.bannerImage);
    } catch (err) {
      setFormError(err);
      setSubmitting(false);
      return;
    }
    // Prepare form data
    const formData = new FormData();
    console.log('Preparing form data with file:', form.bannerImage);
    Object.entries(form).forEach(([key, value]) => {
      if (key === 'bannerImage') {
        console.log('Adding banner image to FormData:', value);
        formData.append('bannerImage', value);
      } else if (key === 'locationTargeting') {
        // Handle locationTargeting object properly
        // FormData automatically converts objects to [object Object] strings
        // So we need to flatten the nested object into individual fields
        if (value && typeof value === 'object') {
          if (value.type === 'radius' && value.coordinates && value.radiusKm) {
            // For radius-based targeting, send individual fields
            formData.append('locationTargeting[type]', value.type);
            formData.append('locationTargeting[coordinates][lat]', value.coordinates.lat);
            formData.append('locationTargeting[coordinates][lng]', value.coordinates.lng);
            formData.append('locationTargeting[radiusKm]', value.radiusKm);
          } else if (value.type === 'none') {
            // For global targeting, just send the type
            formData.append('locationTargeting[type]', value.type);
          }
        }
      } else {
        console.log('Adding field to FormData:', key, value);
        formData.append(key, value);
      }
    });
    // Verify FormData contents
    console.log('=== FormData Contents ===');
    for (let [key, value] of formData.entries()) {
      console.log('FormData entry:', key, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
    }
    console.log('=== End FormData Contents ===');
    try {
      // Add delay to prevent rate limiting
      await addDelay(1000);
      // Don't set Content-Type header for file uploads - let axios handle it
      console.log('Uploading form data:', {
        bannerImage: form.bannerImage?.name,
        client: form.client,
        title: form.title,
        // ... other fields
      });
      await axios.post('/admin/ads', formData, {
        // Let axios automatically set the correct Content-Type with boundary
      });
      setSuccess('Ad created successfully!');
      setForm({
        client: '',
        title: '',
        subtitle: '',
        buttonLabel: '',
        url: '',
        adType: 'Monthly',
        adStartDate: '',
        adEndDate: '',
        priority: 1,
        impressionTrackUrl: '',
        clickTrackUrl: '',
        isSponsored: false,
        isPremiumOnly: false,
        tag: '',
        backgroundColor: '',
        textColor: '',
        ctaStyle: 'filled',
        trackingCode: '',
        visibilityRule: 'all',
        showDismiss: false,
        placement: '',
        bannerImage: null,
        // Reset location targeting to default
        locationTargeting: {
          type: 'none',
          coordinates: null,
          radiusKm: null
        }
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      resetLocationSearch();
      fetchAds();
      setShowCreateForm(false);
    } catch (err) {
      if (err.response?.status === 429) {
        setFormError('Rate limit exceeded. Please wait 15 minutes and try again.');
      } else {
        console.error('Upload error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: err.message
        });
        setFormError('Failed to create ad.');
      }
      console.error('Error creating ad:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Edit logic
  const openEditModal = (ad) => {
    console.log('Opening edit modal for ad:', ad);
    // Ensure location targeting fields are properly initialized
    const adWithLocationTargeting = {
      ...ad,
      locationTargeting: {
        type: ad.locationTargeting?.type || 'none',
        coordinates: ad.locationTargeting?.coordinates || null,
        radiusKm: ad.locationTargeting?.radiusKm || null
      }
    };
    setEditAd(adWithLocationTargeting);
    setShowEditModal(true);
  };
  const closeEditModal = () => {
    setEditAd(null);
    setShowEditModal(false);
  };
  const handleEditChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setEditAd((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      if (files && files[0]) {
        setEditAd((prev) => ({ ...prev, bannerImage: files[0] }));
      }
      // Do not clear bannerImageUrl if file is not selected
    } else {
      setEditAd((prev) => ({ ...prev, [name]: value }));
    }
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editAd) return;
    setFormError('');
    // Validate required fields
    const requiredFields = [
      'client', 'title', 'buttonLabel', 'url', 'adType', 'adStartDate', 'adEndDate',
      'priority', 'ctaStyle', 'visibilityRule', 'placement'
    ];
    for (let field of requiredFields) {
      if (!editAd[field] || (typeof editAd[field] === 'string' && editAd[field].trim() === '')) {
        setFormError(`Field "${field}" is required.`);
        return;
      }
    }
    
    // NEW: Validate location targeting fields for edit
    if (editAd.locationTargeting?.type === 'radius') {
      if (!editAd.locationTargeting.coordinates?.lat || !editAd.locationTargeting.coordinates?.lng) {
        setFormError('Latitude and longitude are required for location-based ads.');
        return;
      }
      if (!editAd.locationTargeting.radiusKm || editAd.locationTargeting.radiusKm <= 0) {
        setFormError('Radius is required and must be greater than 0 for location-based ads.');
        return;
      }
      // Validate coordinate ranges
      if (editAd.locationTargeting.coordinates.lat < -90 || editAd.locationTargeting.coordinates.lat > 90) {
        setFormError('Latitude must be between -90 and 90 degrees.');
        return;
      }
      if (editAd.locationTargeting.coordinates.lng < -180 || editAd.locationTargeting.coordinates.lng > 180) {
        setFormError('Longitude must be between -180 and 180 degrees.');
        return;
      }
      if (editAd.locationTargeting.radiusKm > 1000) {
        setFormError('Radius cannot exceed 1000 kilometers.');
        return;
      }
    }
    if (typeof editAd.isSponsored !== 'boolean') {
      setFormError('Field "isSponsored" is required.');
      return;
    }
    if (typeof editAd.isPremiumOnly !== 'boolean') {
      setFormError('Field "isPremiumOnly" is required.');
      return;
    }
    if (typeof editAd.showDismiss !== 'boolean') {
      setFormError('Field "showDismiss" is required.');
      return;
    }
    // Validate image: must have either a new file or an existing valid image URL
    const hasValidExistingImage = editAd.bannerImageUrl && !editAd.bannerImageUrl.includes('(binary)');
    const hasNewImage = editAd.bannerImage instanceof File;
    if (!hasNewImage && !hasValidExistingImage) {
      setFormError('Banner image is required.');
      return;
    }
    // Validate new image file if selected
    if (editAd.bannerImage instanceof File) {
      try {
        validateImageFile(editAd.bannerImage);
      } catch (err) {
        setFormError(err.message);
        return;
      }
    }
    // If new image, validate dimensions
    if (editAd.bannerImage) {
      try {
        await validateImageDimensions(editAd.bannerImage);
      } catch (err) {
        setFormError(err);
        return;
      }
    }
    const formData = new FormData();
    Object.entries(editAd).forEach(([key, value]) => {
      if (key === 'bannerImage') {
        // Only send bannerImage if it's a new File or if the existing one is valid
        if (value instanceof File) {
          formData.append('bannerImage', value);
        } else if (value && !value.includes('(binary)') && typeof value === 'string') {
          // Don't send the old bannerImage value if it's (binary)
          // Only send if it's a valid filename
        }
      } else if (key === 'locationTargeting') {
        // Handle locationTargeting object properly
        // FormData automatically converts objects to [object Object] strings
        // So we need to flatten the nested object into individual fields
        if (value && typeof value === 'object') {
          if (value.type === 'radius' && value.coordinates && value.radiusKm) {
            // For radius-based targeting, send individual fields
            formData.append('locationTargeting[type]', value.type);
            formData.append('locationTargeting[coordinates][lat]', value.coordinates.lat);
            formData.append('locationTargeting[coordinates][lng]', value.coordinates.lng);
            formData.append('locationTargeting[radiusKm]', value.radiusKm);
          } else if (value.type === 'none') {
            // For global targeting, just send the type
            formData.append('locationTargeting[type]', value.type);
          }
        }
      } else if (key !== 'bannerImage') {
        formData.append(key, value);
      }
    });
    try {
      // Add delay to prevent rate limiting
      await addDelay(1000);
      // Don't set Content-Type header for file uploads - let axios handle it
      console.log('=== Edit FormData Contents ===');
      for (let [key, value] of formData.entries()) {
        console.log('Edit FormData entry:', key, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
      }
      console.log('=== End Edit FormData Contents ===');
      
      console.log('Updating ad with form data:', {
        bannerImage: editAd.bannerImage?.name,
        client: editAd.client,
        title: editAd.title,
        // ... other fields
      });
      await axios.put(`/admin/ads/${editAd._id || editAd.id}`, formData, {
        // Let axios automatically set the correct Content-Type with boundary
      });
      setSuccess('Ad updated successfully!');
      closeEditModal();
      fetchAds();
    } catch (err) {
      if (err.response?.status === 429) {
        setFormError('Rate limit exceeded. Please wait 15 minutes and try again.');
      } else {
        setFormError('Failed to update ad.');
      }
      console.error('Error updating ad:', err);
    }
  };

  // Delete logic
  const openDeleteConfirm = (adId) => {
    setDeleteAdId(adId);
    setShowDeleteConfirm(true);
  };
  const closeDeleteConfirm = () => {
    setDeleteAdId(null);
    setShowDeleteConfirm(false);
  };
  const handleDelete = async () => {
    if (!deleteAdId) return;
    try {
      await axios.delete(`/admin/ads/${deleteAdId}`);
      setSuccess('Ad deleted successfully!');
      closeDeleteConfirm();
      fetchAds();
    } catch (err) {
      setFormError('Failed to delete ad.');
    }
  };

  // Add Ad button handler
  const handleShowCreateForm = () => {
    setShowCreateForm(true);
    setFormError('');
    setSuccess('');
  };
  const handleHideCreateForm = () => {
    setShowCreateForm(false);
    setFormError('');
    setSuccess('');
  };

  const PLACEMENT_OPTIONS = [
    'offer_screen',
    'nutrition_dashboard_screen',
    'workout_screen',
    'water_intake_screen',
    'home_screen',
    'steps_screen',
    'sleep_screen',
    'fall_detection_screen',
    'metrics_screen',
    'hospitals_screen',
    'pharmacies_screen',
    'heart_monitor_screen',
    'mood_screen',
    'symtopms_checker_screen',
    'skin_analysis_screen',
  ];

  return (
    <div className="page-content">
      <h2>Ads Management</h2>
      
      {/* NEW: Location targeting info banner */}
      <div style={{
        padding: '16px',
        backgroundColor: '#e3f2fd',
        border: '1px solid #2196f3',
        borderRadius: '8px',
        marginBottom: '16px',
        fontSize: '0.9em'
      }}>
        <div style={{fontWeight: '500', color: '#1976d2', marginBottom: '8px'}}>
          🎯 New Feature: Location-Based Ad Targeting
        </div>
        <div style={{color: '#1976d2'}}>
          You can now create ads that target users based on their location. Choose between global ads (shown to all users) 
          or location-based ads (shown only to users within a specific radius of your target area).
        </div>
      </div>
      
      {/* NEW: API-based location search info */}
      <div style={{
        padding: '16px',
        backgroundColor: '#e8f5e8',
        border: '1px solid #4caf50',
        borderRadius: '8px',
        marginBottom: '16px',
        fontSize: '0.9em'
      }}>
        <div style={{fontWeight: '500', color: '#2e7d32', marginBottom: '8px'}}>
          🔍 Enhanced Location Search: API + Static Fallback
        </div>
        <div style={{color: '#2e7d32'}}>
          Location search now uses the new <code>/admin/ads/location-search</code> API endpoint with automatic fallback to static city database. 
          Search for any city, landmark, or address - the system will automatically convert it to coordinates!
        </div>
                 <div style={{marginTop: '8px', fontSize: '0.85em', color: '#2e7d32'}}>
           💡 <strong>Try searching for:</strong> "Mumbai", "MG Road Bangalore", "Phoenix Mall", or any address in the location search field below
         </div>
         <div style={{marginTop: '4px', fontSize: '0.75em', color: '#2e7d32'}}>
           ⏱️ Search is debounced (500ms delay) to prevent excessive API calls
         </div>
         <div style={{marginTop: '4px', fontSize: '0.75em', color: '#ff9800'}}>
           🔧 <strong>Note:</strong> API search is currently disabled while backend is being developed. Using local city database.
         </div>
      </div>
      
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
        <button className="btn btn-secondary" onClick={handleRefresh} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh Ads'}
        </button>
      </div>
      {!showCreateForm ? (
        <>
          <button className="btn btn-primary" style={{marginBottom: '1.5rem'}} onClick={handleShowCreateForm}>+ Add Ad</button>
          <div className="card">
            <h3>Active Ads</h3>
            {loading ? (
              <p>Loading ads...</p>
            ) : error ? (
              <div className="alert alert-error">{error}</div>
            ) : (
              <div style={{overflowX:'auto'}}>
                <table style={tableStyles}>
                  <thead>
                    <tr>
                      <th style={thStyles}>Banner</th>
                      <th style={thStyles}>Title</th>
                      <th style={thStyles}>Client</th>
                      <th style={thStyles}>Type</th>
                      <th style={thStyles}>Start</th>
                      <th style={thStyles}>End</th>
                      <th style={thStyles}>Priority</th>
                      <th style={thStyles}>Placement</th>
                      <th style={thStyles}>Location</th>
                      <th style={thStyles}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ads.length === 0 ? (
                      <tr><td colSpan={10} style={tdStyles}>No active ads found.</td></tr>
                    ) : (
                      ads.map((ad, idx) => (
                        <tr key={ad._id || ad.id} style={{background: idx%2===0 ? '#f9fafb' : '#fff'}}>
                          <td style={tdStyles}>
                            {(() => {
                              const imagePath = ad.bannerImageUrl || ad.bannerImage;
                              const hasValidImage = imagePath && !imagePath.includes('(binary)');
                              const finalImageUrl = hasValidImage ? getImageUrl(imagePath) : null;
                              console.log(`Ad "${ad.title}" image:`, {
                                imagePath,
                                hasValidImage,
                                finalUrl: finalImageUrl,
                                willDisplay: hasValidImage ? 'YES' : 'NO'
                              });
                              return hasValidImage ? (
                                <div style={{position: 'relative'}}>
                                  <img 
                                    src={finalImageUrl}
                                    alt="banner" 
                                    style={{width:120, height:60, objectFit:'cover', borderRadius:8, boxShadow:'0 1px 4px #0001'}}
                                    onLoad={() => console.log(`✅ Image loaded successfully: ${finalImageUrl}`)}
                                    onError={(e) => {
                                      console.error(`❌ Image failed to load: ${finalImageUrl}`);
                                      console.error('Image error details:', e);
                                      // Show a placeholder or error indicator
                                      e.target.style.display = 'none';
                                      if (e.target.nextSibling) {
                                        e.target.nextSibling.style.display = 'block';
                                      }
                                    }}
                                  />
                                  <div style={{display:'none', width:120, height:60, background:'#f0f0f0', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', color:'#666'}}>
                                    Image Error
                                  </div>
                                </div>
                              ) : (
                                <div style={{width:120, height:60, background:'#f8f9fa', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', color:'#666', border:'1px dashed #dee2e6'}}>
                                  No image
                                </div>
                              );
                            })()}
                          </td>
                          <td style={tdStyles}>{ad.title}</td>
                          <td style={tdStyles}>{ad.client}</td>
                          <td style={tdStyles}>{ad.adType}</td>
                          <td style={tdStyles}>{ad.adStartDate ? ad.adStartDate.slice(0,10) : ''}</td>
                          <td style={tdStyles}>{ad.adEndDate ? ad.adEndDate.slice(0,10) : ''}</td>
                          <td style={tdStyles}>{ad.priority}</td>
                          <td style={tdStyles}>{ad.placement}</td>
                          <td style={tdStyles}>
                            {/* NEW: Location targeting indicator */}
                            {(() => {
                              const locationType = ad.locationTargeting?.type || 'none';
                              if (locationType === 'radius') {
                                return (
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 8px',
                                    backgroundColor: '#dbeafe',
                                    color: '#1e40af',
                                    borderRadius: '12px',
                                    fontSize: '0.8em',
                                    fontWeight: '500'
                                  }}>
                                    📍 {ad.locationTargeting.radiusKm}km
                                  </div>
                                );
                              } else {
                                return (
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 8px',
                                    backgroundColor: '#d1fae5',
                                    color: '#059669',
                                    borderRadius: '12px',
                                    fontSize: '0.8em',
                                    fontWeight: '500'
                                  }}>
                                    🌍 Global
                                  </div>
                                );
                              }
                            })()}
                          </td>
                          <td style={tdStyles}>
                            <button style={actionBtn} title="Edit" onClick={() => openEditModal(ad)}>
                              ✏️
                            </button>
                            <button style={actionBtn} title="Delete" onClick={() => openDeleteConfirm(ad._id || ad.id)}>
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="card" style={{marginTop:'2rem'}}>
          <h3>Create New Ad</h3>
          {formError && <div style={{color: 'red', marginBottom: 12, fontWeight: 500}}>{formError}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          <form onSubmit={handleSubmit} encType="multipart/form-data" className="ads-form">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'16px'}}>
              <div className="form-group">
                <label>Client*</label>
                <input name="client" value={form.client} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Title*</label>
                <input name="title" value={form.title} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Subtitle</label>
                <input name="subtitle" value={form.subtitle} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Button Label*</label>
                <input name="buttonLabel" value={form.buttonLabel} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>URL*</label>
                <input name="url" value={form.url} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Ad Type*</label>
                <select name="adType" value={form.adType} onChange={handleChange} required>
                  <option value="Monthly">Monthly</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Daily">Daily</option>
                  <option value="One-time">One-time</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ad Start Date*</label>
                <input name="adStartDate" type="date" value={form.adStartDate} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Ad End Date*</label>
                <input name="adEndDate" type="date" value={form.adEndDate} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Priority* (1-10)</label>
                <input name="priority" type="number" min="1" max="10" value={form.priority} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Impression Track URL</label>
                <input name="impressionTrackUrl" value={form.impressionTrackUrl} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Click Track URL</label>
                <input name="clickTrackUrl" value={form.clickTrackUrl} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Is Sponsored*</label>
                <input name="isSponsored" type="checkbox" checked={form.isSponsored} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Is Premium Only*</label>
                <input name="isPremiumOnly" type="checkbox" checked={form.isPremiumOnly} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Tag</label>
                <input name="tag" value={form.tag} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Background Color</label>
                <input name="backgroundColor" value={form.backgroundColor} onChange={handleChange} placeholder="#FFFFFF" />
              </div>
              <div className="form-group">
                <label>Text Color</label>
                <input name="textColor" value={form.textColor} onChange={handleChange} placeholder="#000000" />
              </div>
              <div className="form-group">
                <label>CTA Style*</label>
                <select name="ctaStyle" value={form.ctaStyle} onChange={handleChange} required>
                  <option value="filled">Filled</option>
                  <option value="outlined">Outlined</option>
                  <option value="text">Text</option>
                </select>
              </div>
              <div className="form-group">
                <label>Tracking Code</label>
                <input name="trackingCode" value={form.trackingCode} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Visibility Rule*</label>
                <select name="visibilityRule" value={form.visibilityRule} onChange={handleChange} required>
                  <option value="all">All</option>
                  <option value="premium">Premium</option>
                  <option value="free">Free</option>
                </select>
              </div>
              <div className="form-group">
                <label>Show Dismiss*</label>
                <input name="showDismiss" type="checkbox" checked={form.showDismiss} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Placement*</label>
                <select name="placement" value={form.placement} onChange={handleChange} required>
                  <option value="">Select placement...</option>
                  {PLACEMENT_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{gridColumn:'1/5'}}>
                <label>Banner Image* (800x400px)</label>
                <input name="bannerImage" type="file" accept="image/*" onChange={handleChange} ref={fileInputRef} required />
              </div>
              
              {/* NEW: Location Targeting Section */}
              <div className="form-group" style={{gridColumn:'1/5'}}>
                <label style={{fontWeight: 'bold', fontSize: '1.1em', marginBottom: '8px', display: 'block'}}>Location Targeting</label>
                <div style={{padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6'}}>
                  <div style={{marginBottom: '16px'}}>
                    <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Targeting Type*</label>
                    <div style={{display: 'flex', gap: '16px'}}>
                      <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                        <input
                          type="radio"
                          name="targetingType"
                          value="none"
                          checked={form.locationTargeting.type === 'none'}
                          onChange={(e) => handleLocationTargetingChange('type', e.target.value)}
                        />
                        <span>Global Ad (Show to all users)</span>
                      </label>
                      <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                        <input
                          type="radio"
                          name="targetingType"
                          value="radius"
                          checked={form.locationTargeting.type === 'radius'}
                          onChange={(e) => handleLocationTargetingChange('type', e.target.value)}
                        />
                        <span>Location-Based Ad (Show within specific area)</span>
                      </label>
                    </div>
                  </div>
                  
                  {form.locationTargeting.type === 'radius' && (
                    <>
                      <div style={{marginBottom: '16px'}}>
                        <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Location Search</label>
                        <div className="location-search-container" style={{position: 'relative', marginBottom: '12px'}}>
                          <div style={{position: 'relative'}}>
                            <input
                              type="text"
                              placeholder="Type to search for cities, landmarks, or addresses..."
                              value={locationSearchValue}
                              onChange={(e) => handleLocationSearch(e.target.value)}
                              onFocus={() => {
                                if (locationSearchResults.length > 0) {
                                  setShowLocationDropdown(true);
                                }
                              }}
                              onKeyDown={handleLocationKeyDown}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '0.9em',
                                paddingRight: '60px' // Make room for loading indicator
                              }}
                            />
                            
                                                         {/* Loading indicator */}
                             {locationSearchLoading && (
                               <div style={{
                                 position: 'absolute',
                                 right: '8px',
                                 top: '50%',
                                 transform: 'translateY(-50%)',
                                 fontSize: '14px',
                                 color: '#007bff'
                               }}>
                                 🔄
                               </div>
                             )}
                             
                             {/* Fallback indicator */}
                             {locationSearchError && !locationSearchLoading && (
                               <div style={{
                                 position: 'absolute',
                                 right: '8px',
                                 top: '50%',
                                 transform: 'translateY(-50%)',
                                 fontSize: '12px',
                                 color: '#ff9800',
                                 backgroundColor: '#fff3e0',
                                 padding: '2px 6px',
                                 borderRadius: '3px',
                                 border: '1px solid #ffb74d'
                               }}>
                                 ⚠️
                               </div>
                             )}
                             
                             {/* Debouncing indicator */}
                             {locationSearchValue && locationSearchValue.length >= 2 && !locationSearchLoading && debouncedSearchValue !== locationSearchValue && (
                               <div style={{
                                 position: 'absolute',
                                 right: '8px',
                                 top: '50%',
                                 transform: 'translateY(-50%)',
                                 fontSize: '12px',
                                 color: '#6c757d'
                               }}>
                                 ⏱️
                               </div>
                             )}
                          </div>
                          
                          {/* Error message */}
                          {locationSearchError && (
                            <div style={{
                              marginTop: '4px',
                              fontSize: '0.8em',
                              color: '#dc3545',
                              padding: '4px 8px',
                              backgroundColor: '#f8d7da',
                              borderRadius: '4px',
                              border: '1px solid #f5c6cb'
                            }}>
                              ⚠️ {locationSearchError}
                            </div>
                          )}
                          
                          {/* Auto-search dropdown */}
                          {showLocationDropdown && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              zIndex: 1000,
                              maxHeight: '200px',
                              overflowY: 'auto'
                            }}>
                              {locationSearchResults.map((location, index) => (
                                <div
                                  key={location.id || index}
                                  onClick={() => selectLocation(location)}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '0.9em',
                                    backgroundColor: index === selectedLocationIndex ? '#f5f5f5' : 'white'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f5f5f5';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'white';
                                  }}
                                >
                                  <div style={{fontWeight: '500'}}>{location.display}</div>
                                  <div style={{fontSize: '0.8em', color: '#666'}}>
                                    {location.lat}, {location.lng}
                                    {location.distance && (
                                      <span style={{marginLeft: '8px', color: '#007bff'}}>
                                        📍 {location.distance}km away
                                      </span>
                                    )}
                                    {location.type === 'api_result' && (
                                      <span style={{marginLeft: '8px', color: '#28a745', fontSize: '0.75em'}}>
                                        🌐 API Result
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div style={{fontSize: '0.85em', color: '#666', marginBottom: '8px'}}>
                          💡 Start typing to search cities, landmarks, or addresses. API results show distance information.
                        </div>
                        
                        {/* Status indicator */}
                        {locationSearchError && (
                          <div style={{
                            fontSize: '0.8em',
                            color: '#ff9800',
                            backgroundColor: '#fff3e0',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            border: '1px solid #ffb74d',
                            marginBottom: '12px'
                          }}>
                            ⚠️ {locationSearchError}
                          </div>
                        )}
                        
                        {/* NEW: Advanced Search Options */}
                        <div style={{marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef'}}>
                          <div style={{fontSize: '0.9em', fontWeight: '500', marginBottom: '8px', color: '#495057'}}>
                            🔍 Advanced Search Options
                          </div>
                          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                            <div>
                              <label style={{fontSize: '0.8em', color: '#6c757d', marginBottom: '4px', display: 'block'}}>Search Radius</label>
                              <select 
                                onChange={(e) => {
                                  // This could be used for API search radius
                                  console.log('Search radius changed to:', e.target.value);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '6px',
                                  border: '1px solid #ced4da',
                                  borderRadius: '4px',
                                  fontSize: '0.8em'
                                }}
                              >
                                <option value="5">5 km</option>
                                <option value="10" selected>10 km</option>
                                <option value="20">20 km</option>
                                <option value="50">50 km</option>
                                <option value="100">100 km</option>
                              </select>
                            </div>
                            <div>
                              <label style={{fontSize: '0.8em', color: '#6c757d', marginBottom: '4px', display: 'block'}}>Search Type</label>
                              <select 
                                onChange={(e) => {
                                  // This could be used to filter search types
                                  console.log('Search type changed to:', e.target.value);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '6px',
                                  border: '1px solid #ced4da',
                                  borderRadius: '4px',
                                  fontSize: '0.8em'
                                }}
                              >
                                <option value="all" selected>All Locations</option>
                                <option value="cities">Cities Only</option>
                                <option value="landmarks">Landmarks Only</option>
                                <option value="addresses">Addresses Only</option>
                              </select>
                            </div>
                          </div>
                          <div style={{fontSize: '0.75em', color: '#6c757d', marginTop: '8px'}}>
                            💡 These options will be used for API-based location search
                          </div>
                        </div>
                        
                        <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Location Coordinates*</label>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                          <div>
                            <label style={{fontSize: '0.9em', color: '#666'}}>Latitude</label>
                            <input
                              type="number"
                              step="0.0001"
                              min="-90"
                              max="90"
                              placeholder="e.g., 19.0760"
                              value={form.locationTargeting.coordinates?.lat || ''}
                              onChange={(e) => handleCoordinateChange('lat', e.target.value)}
                              style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                            />
                          </div>
                          <div>
                            <label style={{fontSize: '0.9em', color: '#666'}}>Longitude</label>
                            <input
                              type="number"
                              step="0.0001"
                              min="-180"
                              max="180"
                              placeholder="e.g., 72.8777"
                              value={form.locationTargeting.coordinates?.lng || ''}
                              onChange={(e) => handleCoordinateChange('lng', e.target.value)}
                              style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                            />
                          </div>
                        </div>
                        <div style={{marginTop: '8px', fontSize: '0.85em', color: '#666'}}>
                          Enter the coordinates of the center point for your target area
                        </div>
                      </div>
                      
                      <div style={{marginBottom: '16px'}}>
                        <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Target Radius* (kilometers)</label>
                        <input
                          type="number"
                          min="0.1"
                          max="1000"
                          step="0.1"
                          placeholder="e.g., 5"
                          value={form.locationTargeting.radiusKm || ''}
                          onChange={(e) => handleLocationTargetingChange('radiusKm', parseFloat(e.target.value) || null)}
                          style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                        />
                        <div style={{marginTop: '8px', fontSize: '0.85em', color: '#666'}}>
                          Your ad will be shown to users within this radius from the specified location
                        </div>
                      </div>
                      
                      {form.locationTargeting.coordinates?.lat && form.locationTargeting.coordinates?.lng && form.locationTargeting.radiusKm && (
                        <div style={{padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '6px', border: '1px solid #2196f3'}}>
                          <div style={{fontWeight: '500', color: '#1976d2', marginBottom: '4px'}}>📍 Location Preview</div>
                          <div style={{fontSize: '0.9em', color: '#1976d2'}}>
                            This ad will target users within <strong>{form.locationTargeting.radiusKm}km</strong> of 
                            coordinates <strong>({form.locationTargeting.coordinates.lat}, {form.locationTargeting.coordinates.lng})</strong>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {form.locationTargeting.type === 'none' && (
                    <div style={{padding: '12px', backgroundColor: '#e8f5e8', borderRadius: '6px', border: '1px solid #4caf50'}}>
                      <div style={{fontWeight: '500', color: '#2e7d32', marginBottom: '4px'}}>🌍 Global Targeting</div>
                      <div style={{fontSize: '0.9em', color: '#2e7d32'}}>
                        This ad will be shown to all users regardless of their location
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{display:'flex',gap:12,marginTop:24}}>
              <button type="button" className="btn btn-secondary" onClick={handleHideCreateForm}>Back to List</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Submitting...' : 'Create Ad'}</button>
            </div>
          </form>
        </div>
      )}
      {/* Edit Modal */}
      {showEditModal && editAd && (
        <div className="modal-overlay" style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'#0006',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div className="modal-content" style={{background:'#fff',padding:32,borderRadius:12,minWidth:320,maxWidth:800,maxHeight:'80vh',overflowY:'auto',boxShadow:'0 4px 24px #0002',position:'relative'}}>
            <button onClick={closeEditModal} style={{position:'absolute',top:12,right:12,fontSize:20,border:'none',background:'none',cursor:'pointer'}}>×</button>
            <h3>Edit Ad</h3>
            <form onSubmit={handleEditSubmit} encType="multipart/form-data" className="ads-form">
              {formError && (
                <div style={{color: 'red', marginBottom: 12, fontWeight: 500}}>{formError}</div>
              )}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'16px'}}>
                <div className="form-group">
                  <label>Client*</label>
                  <input name="client" value={editAd.client} onChange={handleEditChange} required />
                </div>
                <div className="form-group">
                  <label>Title*</label>
                  <input name="title" value={editAd.title} onChange={handleEditChange} required />
                </div>
                <div className="form-group">
                  <label>Subtitle</label>
                  <input name="subtitle" value={editAd.subtitle || ''} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Button Label*</label>
                  <input name="buttonLabel" value={editAd.buttonLabel} onChange={handleEditChange} required />
                </div>
                <div className="form-group">
                  <label>URL*</label>
                  <input name="url" value={editAd.url} onChange={handleEditChange} required />
                </div>
                <div className="form-group">
                  <label>Ad Type*</label>
                  <select name="adType" value={editAd.adType} onChange={handleEditChange} required>
                    <option value="Monthly">Monthly</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Daily">Daily</option>
                    <option value="One-time">One-time</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Ad Start Date*</label>
                  <input name="adStartDate" type="date" value={editAd.adStartDate ? editAd.adStartDate.slice(0,10) : ''} onChange={handleEditChange} required />
                </div>
                <div className="form-group">
                  <label>Ad End Date*</label>
                  <input name="adEndDate" type="date" value={editAd.adEndDate ? editAd.adEndDate.slice(0,10) : ''} onChange={handleEditChange} required />
                </div>
                <div className="form-group">
                  <label>Priority* (1-10)</label>
                  <input name="priority" type="number" min="1" max="10" value={editAd.priority} onChange={handleEditChange} required />
                </div>
                <div className="form-group">
                  <label>Impression Track URL</label>
                  <input name="impressionTrackUrl" value={editAd.impressionTrackUrl || ''} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Click Track URL</label>
                  <input name="clickTrackUrl" value={editAd.clickTrackUrl || ''} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Is Sponsored*</label>
                  <input name="isSponsored" type="checkbox" checked={!!editAd.isSponsored} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Is Premium Only*</label>
                  <input name="isPremiumOnly" type="checkbox" checked={!!editAd.isPremiumOnly} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Tag</label>
                  <input name="tag" value={editAd.tag || ''} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Background Color</label>
                  <input name="backgroundColor" value={editAd.backgroundColor || ''} onChange={handleEditChange} placeholder="#FFFFFF" />
                </div>
                <div className="form-group">
                  <label>Text Color</label>
                  <input name="textColor" value={editAd.textColor || ''} onChange={handleEditChange} placeholder="#000000" />
                </div>
                <div className="form-group">
                  <label>CTA Style*</label>
                  <select name="ctaStyle" value={editAd.ctaStyle} onChange={handleEditChange} required>
                    <option value="filled">Filled</option>
                    <option value="outlined">Outlined</option>
                    <option value="text">Text</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tracking Code</label>
                  <input name="trackingCode" value={editAd.trackingCode || ''} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Visibility Rule*</label>
                  <select name="visibilityRule" value={editAd.visibilityRule} onChange={handleEditChange} required>
                    <option value="all">All</option>
                    <option value="premium">Premium</option>
                    <option value="free">Free</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Show Dismiss*</label>
                  <input name="showDismiss" type="checkbox" checked={!!editAd.showDismiss} onChange={handleEditChange} />
                </div>
                <div className="form-group">
                  <label>Placement*</label>
                  <select name="placement" value={editAd.placement} onChange={handleEditChange} required>
                    <option value="">Select placement...</option>
                    {PLACEMENT_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{gridColumn:'1/5'}}>
                  <label>Banner Image (800x400px)</label>
                  <input name="bannerImage" type="file" accept="image/*" onChange={handleEditChange} />
                  {editAd.bannerImageUrl && editAd.bannerImageUrl.includes('(binary)') && (
                    <div style={{marginTop:8, padding:8, backgroundColor:'#fff3cd', border:'1px solid #ffeaa7', borderRadius:4, color:'#856404'}}>
                      ⚠️ Current image is corrupted. Please upload a new image.
                    </div>
                  )}
                  {/* Show only file name if present */}
                  {editAd.bannerImage instanceof File ? (
                    <div style={{marginTop:8, fontSize:'0.95em', color:'#555'}}>
                      Selected: {editAd.bannerImage.name}
                    </div>
                  ) : editAd.bannerImageUrl || editAd.bannerImage ? (
                    <div style={{marginTop:8, fontSize:'0.95em', color:'#555'}}>
                      Current: {(editAd.bannerImageUrl || editAd.bannerImage).includes('(binary)') ? (
                        'Corrupted image'
                      ) : (
                        <a 
                          href={getImageUrl(editAd.bannerImageUrl || editAd.bannerImage)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{color: '#007bff', textDecoration: 'underline'}}
                        >
                          {(editAd.bannerImageUrl || editAd.bannerImage).split('/').pop()}
                        </a>
                      )}
                    </div>
                  ) : null}
                </div>
                
                {/* NEW: Location Targeting Section for Edit */}
                <div className="form-group" style={{gridColumn:'1/5'}}>
                  <label style={{fontWeight: 'bold', fontSize: '1.1em', marginBottom: '8px', display: 'block'}}>Location Targeting</label>
                  <div style={{padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6'}}>
                    <div style={{marginBottom: '16px'}}>
                      <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Targeting Type*</label>
                      <div style={{display: 'flex', gap: '16px'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                          <input
                            type="radio"
                            name="editTargetingType"
                            value="none"
                            checked={(editAd.locationTargeting?.type || 'none') === 'none'}
                            onChange={(e) => handleEditLocationTargetingChange('type', e.target.value)}
                          />
                          <span>Global Ad (Show to all users)</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                          <input
                            type="radio"
                            name="editTargetingType"
                            value="radius"
                            checked={(editAd.locationTargeting?.type || 'none') === 'radius'}
                            onChange={(e) => handleEditLocationTargetingChange('type', e.target.value)}
                          />
                          <span>Location-Based Ad (Show within specific area)</span>
                        </label>
                      </div>
                    </div>
                    
                    {(editAd.locationTargeting?.type || 'none') === 'radius' && (
                      <>
                        <div style={{marginBottom: '16px'}}>
                          <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Location Search</label>
                          <div className="location-search-container" style={{position: 'relative', marginBottom: '12px'}}>
                            <input
                              type="text"
                              placeholder="Type to search for cities (e.g., Mumbai, Delhi, Bangalore)"
                              onChange={(e) => handleEditLocationSearch(e.target.value)}
                              onFocus={() => {
                                if (locationSearchResults.length > 0) {
                                  setShowLocationDropdown(true);
                                }
                              }}
                              onKeyDown={handleLocationKeyDown}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '0.9em'
                              }}
                            />
                          </div>
                          
                          <div style={{fontSize: '0.85em', color: '#666', marginBottom: '8px'}}>
                            💡 Type a city name to auto-fill coordinates, or enter coordinates manually below.
                          </div>
                          
                          <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Location Coordinates*</label>
                          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                            <div>
                              <label style={{fontSize: '0.9em', color: '#666'}}>Latitude</label>
                              <input
                                type="number"
                                step="0.0001"
                                min="-90"
                                max="90"
                                placeholder="e.g., 19.0760"
                                value={editAd.locationTargeting?.coordinates?.lat || ''}
                                onChange={(e) => handleEditCoordinateChange('lat', e.target.value)}
                                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                              />
                            </div>
                            <div>
                              <label style={{fontSize: '0.9em', color: '#666'}}>Longitude</label>
                              <input
                                type="number"
                                step="0.0001"
                                min="-180"
                                max="180"
                                placeholder="e.g., 72.8777"
                                value={editAd.locationTargeting?.coordinates?.lng || ''}
                                onChange={(e) => handleEditCoordinateChange('lng', e.target.value)}
                                style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                              />
                            </div>
                          </div>
                          <div style={{marginTop: '8px', fontSize: '0.85em', color: '#666'}}>
                            Enter the coordinates of the center point for your target area
                          </div>
                        </div>
                        
                        <div style={{marginBottom: '16px'}}>
                          <label style={{display: 'block', marginBottom: '8px', fontWeight: '500'}}>Target Radius* (kilometers)</label>
                          <input
                            type="number"
                            min="0.1"
                            max="1000"
                            step="0.1"
                            placeholder="e.g., 5"
                            value={editAd.locationTargeting?.radiusKm || ''}
                            onChange={(e) => handleEditLocationTargetingChange('radiusKm', parseFloat(e.target.value) || null)}
                            style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                          />
                          <div style={{marginTop: '8px', fontSize: '0.85em', color: '#666'}}>
                            Your ad will be shown to users within this radius from the specified location
                          </div>
                        </div>
                        
                        {editAd.locationTargeting?.coordinates?.lat && editAd.locationTargeting?.coordinates?.lng && editAd.locationTargeting?.radiusKm && (
                          <div style={{padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '6px', border: '1px solid #2196f3'}}>
                            <div style={{fontWeight: '500', color: '#1976d2', marginBottom: '4px'}}>📍 Location Preview</div>
                            <div style={{fontSize: '0.9em', color: '#1976d2'}}>
                              This ad will target users within <strong>{editAd.locationTargeting.radiusKm}km</strong> of 
                              coordinates <strong>({editAd.locationTargeting.coordinates.lat}, {editAd.locationTargeting.coordinates.lng})</strong>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    
                    {(editAd.locationTargeting?.type || 'none') === 'none' && (
                      <div style={{padding: '12px', backgroundColor: '#e8f5e8', borderRadius: '6px', border: '1px solid #4caf50'}}>
                        <div style={{fontWeight: '500', color: '#2e7d32', marginBottom: '4px'}}>🌍 Global Targeting</div>
                        <div style={{fontSize: '0.9em', color: '#2e7d32'}}>
                          This ad will be shown to all users regardless of their location
                        </div>
                        </div>
                    )}
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-primary" style={{marginTop:24}}>Update Ad</button>
            </form>
          </div>
        </div>
      )}
      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'#0006',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div className="modal-content" style={{background:'#fff',padding:32,borderRadius:12,minWidth:320,maxWidth:400,boxShadow:'0 4px 24px #0002',position:'relative'}}>
            <h3>Delete Ad</h3>
            <p>Are you sure you want to delete this ad? This action cannot be undone.</p>
            <div style={{display:'flex',justifyContent:'flex-end',gap:12,marginTop:24}}>
              <button className="btn btn-secondary" onClick={closeDeleteConfirm}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdsPage; 